import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBrandSelection } from '@/hooks/useBrandSelection';
import { toastEdgeFunctionError } from '@/utils/toastEdgeFunction';
import { retryWithBackoff } from '@/utils/retryWithBackoff';
import { invokeWithTimeout } from '@/utils/edgeFunctionWithTimeout';
import type {
  CrawlResult,
  TokenizationResult,
  EnrichmentResult,
  GSCMatchResult,
  ScoringResult,
  InternalLinkingOpportunity,
  AnalysisSessionStats,
} from '@/types/seoInternalLinking';
import type { JobStatus } from '@/types/jobQueue';

type AnalysisStep =
  | 'idle'
  | 'crawling'
  | 'tokenizing'
  | 'enriching'
  | 'matching'
  | 'scoring'
  | 'completed'
  | 'error';

interface InternalLinkingContextType {
  // Analysis state
  isAnalyzing: boolean;
  currentStep: AnalysisStep;
  progress: number;
  sessionId: string | null;
  error: string | null;
  
  // Job queue state
  jobId: string | null;
  jobStatus: JobStatus | null;
  useJobQueue: boolean;
  setUseJobQueue: (value: boolean) => void;
  retryCount: number;
  maxRetries: number;
  
  // Session stats for polling
  sessionStats: AnalysisSessionStats;
  
  // Results
  opportunities: InternalLinkingOpportunity[];
  tokensByPage: Record<string, any[]>;
  
  // Direct pipeline methods
  startAnalysis: (
    brandId: string,
    siteUrl: string,
    maxDepth?: number,
    limit?: number,
    filter?: 'full' | 'month' | 'year' | 'custom',
    customLimit?: number
  ) => Promise<CrawlResult | null>;
  tokenizePages: (brandId: string, sessionIdOverride?: string) => Promise<TokenizationResult | null>;
  enrichKeywords: (brandId: string, keywords: string[]) => Promise<EnrichmentResult | null>;
  matchGSC: (brandId: string, keywords: string[], siteUrl: string) => Promise<GSCMatchResult | null>;
  calculateScores: (brandId: string, sessionIdOverride?: string) => Promise<ScoringResult | null>;
  
  // Job queue methods
  startAnalysisWithJobQueue: (
    brandId: string,
    siteUrl: string,
    maxDepth?: number,
    limit?: number,
    filter?: 'full' | 'month' | 'year' | 'custom',
    customLimit?: number
  ) => Promise<string | null>;
  cancelJob: (jobId: string) => Promise<void>;
  cancelAnalysis: () => void;
  
  // Utility
  reset: () => void;
  setOpportunities: (opps: InternalLinkingOpportunity[]) => void;
  setTokensByPage: (tokens: Record<string, any[]>) => void;
  setSessionStats: (stats: AnalysisSessionStats) => void;
  setCurrentSessionId: (id: string | null) => void;
  currentSessionId: string | null;
}

const InternalLinkingContext = createContext<InternalLinkingContextType | undefined>(undefined);

export function InternalLinkingProvider({ children }: { children: React.ReactNode }) {
  const { currentBrandId } = useBrandSelection();
  const { toast } = useToast();
  
  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState<AnalysisStep>('idle');
  const [progress, setProgress] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Job queue state
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [useJobQueue, setUseJobQueue] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Session stats
  const [sessionStats, setSessionStats] = useState<AnalysisSessionStats>({});
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  
  // Results
  const [opportunities, setOpportunities] = useState<InternalLinkingOpportunity[]>([]);
  const [tokensByPage, setTokensByPage] = useState<Record<string, any[]>>({});

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollJobStatus = useCallback(async (currentJobId: string) => {
    try {
      const { data, error } = await invokeWithTimeout(
        supabase,
        'seo-internal-linking-job-status',
        { body: { jobId: currentJobId } }
      );
      
      if (error) {
        console.error('Job status error:', error);
        return;
      }
      
      setJobStatus(data.status);
      setProgress(data.progress || 0);
      
      const stepMap: Record<string, AnalysisStep> = {
        'crawling': 'crawling',
        'crawling_complete': 'crawling',
        'tokenizing': 'tokenizing',
        'tokenizing_complete': 'tokenizing',
        'enriching': 'enriching',
        'enriching_complete': 'enriching',
        'matching_gsc': 'matching',
        'gsc_matching_complete': 'matching',
        'scoring': 'scoring',
        'completed': 'completed',
      };
      
      setCurrentStep(stepMap[data.currentStep] || 'idle');
      
      if (data.status === 'completed') {
        setIsAnalyzing(false);
        setProgress(100);
        setCurrentStep('completed');
        stopPolling();
        localStorage.removeItem('internalLinkingJobId');
        
        toast({
          title: 'Analysis Complete',
          description: 'Internal linking analysis finished successfully!',
        });
        
        return data.results;
      } else if (data.status === 'failed') {
        setIsAnalyzing(false);
        setError(data.error || 'Job failed');
        setCurrentStep('error');
        stopPolling();
        localStorage.removeItem('internalLinkingJobId');
        
        toast({
          title: 'Analysis Failed',
          description: data.error || 'The analysis job failed',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Polling error:', err);
    }
  }, [stopPolling, toast]);

  const resumeJob = useCallback((savedJobId: string) => {
    setJobId(savedJobId);
    setIsAnalyzing(true);
    
    toast({
      title: 'Resuming Analysis',
      description: 'Checking job status...',
    });
    
    pollingIntervalRef.current = setInterval(() => {
      pollJobStatus(savedJobId);
    }, 3000);
    
    pollJobStatus(savedJobId);
  }, [pollJobStatus, toast]);

  // Resume job on mount if exists
  useEffect(() => {
    const savedJobId = localStorage.getItem('internalLinkingJobId');
    if (savedJobId && !jobId) {
      resumeJob(savedJobId);
    }
    
    return () => {
      stopPolling();
    };
  }, []);

  const startAnalysis = useCallback(async (
    brandId: string,
    siteUrl: string,
    maxDepth: number = 3,
    limit: number = 50,
    filter?: 'full' | 'month' | 'year' | 'custom',
    customLimit?: number
  ): Promise<CrawlResult | null> => {
    try {
      setIsAnalyzing(true);
      setCurrentStep('crawling');
      setProgress(10);
      setError(null);

      toast({
        title: 'Starting Analysis',
        description: 'Crawling your website with Firecrawl...',
      });

      const { data, error } = await supabase.functions.invoke(
        'seo-internal-linking-crawl',
        {
          body: { brandId, siteUrl, maxDepth, limit, filter, customLimit },
        }
      );

      if (error) throw error;

      if (data.success) {
        setSessionId(data.sessionId);
        setProgress(20);
        toast({
          title: 'Crawl Complete',
          description: `Successfully crawled ${data.pagesCrawled} pages`,
        });
        return data as CrawlResult;
      }

      throw new Error('Crawl failed');
    } catch (err: any) {
      console.error('Crawl error:', err);
      const parsed = toastEdgeFunctionError('seo-internal-linking-crawl', err, 'Crawl Failed');
      setError(parsed);
      setCurrentStep('error');
      setIsAnalyzing(false);
      return null;
    }
  }, [toast]);

  const tokenizePages = useCallback(async (
    brandId: string,
    sessionIdOverride?: string
  ): Promise<TokenizationResult | null> => {
    try {
      setCurrentStep('tokenizing');
      setProgress(40);

      toast({
        title: 'Tokenizing Content',
        description: 'Extracting keywords in batches...',
      });

      let offset = 0;
      const limit = 8;
      let aggregated: Record<string, any[]> = {};
      let totalExtracted = 0;
      let totalPages = 0;
      let done = false;

      while (!done) {
        const { data, error } = await supabase.functions.invoke(
          'seo-internal-linking-tokenize',
          {
            body: { brandId, sessionId: sessionIdOverride || sessionId, offset, limit },
          }
        );

        if (error) throw error;
        if (!data?.success) throw new Error('Tokenization failed');

        const batchTokens: Record<string, any[]> = data.tokensByPage || {};
        Object.keys(batchTokens).forEach((url) => {
          aggregated[url] = [...(aggregated[url] || []), ...batchTokens[url]];
        });

        totalExtracted += data.tokensExtracted || 0;
        totalPages = data.totalPages || totalPages;
        offset = data.nextOffset ?? offset + limit;
        done = !!data.done;

        if (totalPages) {
          const processed = Math.min(offset, totalPages);
          const pct = 40 + Math.min(15, Math.round((processed / totalPages) * 15));
          setProgress(pct);
        }
      }

      setProgress(55);
      setTokensByPage(aggregated);
      toast({
        title: 'Tokenization Complete',
        description: `Extracted ${totalExtracted} keywords`,
      });

      return { tokensExtracted: totalExtracted, tokensByPage: aggregated } as TokenizationResult;
    } catch (err: any) {
      console.error('Tokenization error:', err);
      const parsed = toastEdgeFunctionError('seo-internal-linking-tokenize', err, 'Tokenization Failed');
      setError(parsed);
      setCurrentStep('error');
      setIsAnalyzing(false);
      return null;
    }
  }, [sessionId, toast]);

  const enrichKeywords = useCallback(async (
    brandId: string,
    keywords: string[]
  ): Promise<EnrichmentResult | null> => {
    try {
      setCurrentStep('enriching');
      setProgress(60);

      toast({
        title: 'Enriching Keywords',
        description: 'Fetching search volume and difficulty from DataForSEO...',
      });

      const { data, error } = await supabase.functions.invoke(
        'seo-internal-linking-enrich-keywords',
        {
          body: { brandId, keywords },
        }
      );

      if (error) throw error;

      if (data.success) {
        setProgress(70);
        toast({
          title: 'Enrichment Complete',
          description: `Enriched ${data.enrichedCount} keywords`,
        });
        return data as EnrichmentResult;
      }

      throw new Error('Enrichment failed');
    } catch (err: any) {
      console.error('Enrichment error:', err);
      const parsed = toastEdgeFunctionError('seo-internal-linking-enrich-keywords', err, 'Enrichment Failed');
      setError(parsed);
      setCurrentStep('error');
      setIsAnalyzing(false);
      return null;
    }
  }, [toast]);

  const matchGSC = useCallback(async (
    brandId: string,
    keywords: string[],
    siteUrl: string
  ): Promise<GSCMatchResult | null> => {
    try {
      setCurrentStep('matching');
      setProgress(80);

      toast({
        title: 'Matching GSC Data',
        description: 'Matching keywords with Google Search Console...',
      });

      const { data, error } = await supabase.functions.invoke(
        'seo-internal-linking-match-gsc',
        {
          body: { brandId, sessionId },
        }
      );

      if (error) throw error;

      if (data.success) {
        setProgress(85);
        toast({
          title: 'GSC Matching Complete',
          description: `Matched ${data.matchedKeywords} keywords with GSC data`,
        });
        return data as GSCMatchResult;
      }

      throw new Error('GSC matching failed');
    } catch (err: any) {
      console.error('GSC matching error:', err);
      const errorMessage = err?.message || '';
      const isGSCAuthError = errorMessage.includes('401') ||
                             errorMessage.includes('authentication') ||
                             errorMessage.includes('UNAUTHENTICATED') ||
                             errorMessage.includes('Invalid Credentials');
      if (isGSCAuthError) {
        setError('GSC authentication failed. Please reconnect Google Search Console in Settings → SEO Settings.');
        toast({
          title: 'GSC Authentication Error',
          description: 'Please reconnect to Google Search Console in Settings → SEO Settings.',
          variant: 'destructive',
        });
      } else {
        const parsed = toastEdgeFunctionError('seo-internal-linking-match-gsc', err, 'GSC Matching Failed');
        setError(parsed);
      }
      setCurrentStep('error');
      setIsAnalyzing(false);
      return null;
    }
  }, [sessionId, toast]);

  const calculateScores = useCallback(async (
    brandId: string,
    sessionIdOverride?: string
  ): Promise<ScoringResult | null> => {
    try {
      setCurrentStep('scoring');
      setProgress(90);

      toast({
        title: 'Calculating Scores',
        description: 'Finding high-priority link opportunities...',
      });

      const { data, error } = await supabase.functions.invoke(
        'seo-internal-linking-calculate-scores',
        {
          body: { brandId, sessionId: sessionIdOverride || sessionId },
        }
      );

      if (error) throw error;

      if (data.success) {
        setProgress(100);
        setCurrentStep('completed');
        setOpportunities(data.opportunities);
        toast({
          title: 'Analysis Complete!',
          description: `Found ${data.opportunities.length} link opportunities`,
        });
        setIsAnalyzing(false);
        return data as ScoringResult;
      }

      throw new Error('Score calculation failed');
    } catch (err: any) {
      console.error('Scoring error:', err);
      const parsed = toastEdgeFunctionError('seo-internal-linking-calculate-scores', err, 'Scoring Failed');
      setError(parsed);
      setCurrentStep('error');
      setIsAnalyzing(false);
      return null;
    }
  }, [sessionId, toast]);

  const startAnalysisWithJobQueue = useCallback(async (
    brandId: string,
    siteUrl: string,
    maxDepth: number = 3,
    limit: number = 50,
    filter?: 'full' | 'month' | 'year' | 'custom',
    customLimit?: number
  ): Promise<string | null> => {
    try {
      setIsAnalyzing(true);
      setCurrentStep('crawling');
      setProgress(5);
      setError(null);
      
      toast({
        title: 'Starting Background Analysis',
        description: 'Creating job queue entry...',
      });
      
      const { result, attempts } = await retryWithBackoff(
        async () => {
          const { data, error } = await invokeWithTimeout(
            supabase,
            'seo-internal-linking-start-job',
            {
              body: { brandId, siteUrl, maxDepth, limit, filter, customLimit },
              timeout: 10000,
            }
          );
          
          if (error) throw error;
          return data;
        },
        {
          maxRetries,
          onRetry: (attempt, err) => {
            setRetryCount(attempt);
            toast({
              title: `Retry ${attempt}/${maxRetries}`,
              description: 'Retrying job creation...',
            });
          },
        }
      );
      
      if (!result?.jobId) {
        throw new Error('No job ID returned');
      }
      
      const newJobId = result.jobId;
      setJobId(newJobId);
      setJobStatus('pending');
      localStorage.setItem('internalLinkingJobId', newJobId);
      
      toast({
        title: 'Job Created',
        description: 'Analysis running in background. Safe to navigate away!',
      });
      
      pollingIntervalRef.current = setInterval(() => {
        pollJobStatus(newJobId);
      }, 3000);
      
      pollJobStatus(newJobId);
      
      return newJobId;
    } catch (err: any) {
      console.error('Job creation error:', err);
      const errorMsg = err.message || 'Failed to create analysis job';
      setError(errorMsg);
      setCurrentStep('error');
      setIsAnalyzing(false);
      
      toast({
        title: 'Job Creation Failed',
        description: errorMsg,
        variant: 'destructive',
      });
      
      return null;
    }
  }, [pollJobStatus, toast]);

  const cancelJob = useCallback(async (currentJobId: string) => {
    try {
      await supabase
        .from('job_queue')
        .update({ status: 'cancelled' })
        .eq('id', currentJobId);
      
      setJobStatus('cancelled');
      setIsAnalyzing(false);
      stopPolling();
      localStorage.removeItem('internalLinkingJobId');
      
      toast({
        title: 'Job Cancelled',
        description: 'Analysis has been stopped',
      });
    } catch (err) {
      console.error('Cancel error:', err);
    }
  }, [stopPolling, toast]);

  const cancelAnalysis = useCallback(() => {
    setIsAnalyzing(false);
    setCurrentStep('idle');
    setProgress(0);
    setError(null);
    
    toast({
      title: 'Analysis Cancelled',
      description: 'The analysis has been stopped',
    });
  }, [toast]);

  const reset = useCallback(() => {
    setIsAnalyzing(false);
    setCurrentStep('idle');
    setProgress(0);
    setSessionId(null);
    setError(null);
    setJobId(null);
    setJobStatus(null);
    setRetryCount(0);
    setOpportunities([]);
    setTokensByPage({});
    setSessionStats({});
    stopPolling();
  }, [stopPolling]);

  return (
    <InternalLinkingContext.Provider
      value={{
        // Analysis state
        isAnalyzing,
        currentStep,
        progress,
        sessionId,
        error,
        
        // Job queue state
        jobId,
        jobStatus,
        useJobQueue,
        setUseJobQueue,
        retryCount,
        maxRetries,
        
        // Session stats
        sessionStats,
        
        // Results
        opportunities,
        tokensByPage,
        
        // Direct pipeline methods
        startAnalysis,
        tokenizePages,
        enrichKeywords,
        matchGSC,
        calculateScores,
        
        // Job queue methods
        startAnalysisWithJobQueue,
        cancelJob,
        cancelAnalysis,
        
        // Utility
        reset,
        setOpportunities,
        setTokensByPage,
        setSessionStats,
        setCurrentSessionId,
        currentSessionId,
      }}
    >
      {children}
    </InternalLinkingContext.Provider>
  );
}

export function useInternalLinkingContext() {
  const context = useContext(InternalLinkingContext);
  if (context === undefined) {
    throw new Error('useInternalLinkingContext must be used within an InternalLinkingProvider');
  }
  return context;
}

